import React, { FC, useCallback, useState } from 'react';
import { Button, Grid, InputAdornment } from '@mui/material';
import { Field, Form } from 'react-final-form';
import Box from '@mui/material/Box';
import TextFieldAdapter from '../FinalFormAdapters/TextFieldAdapter';
import {
  composeValidators,
  parseNumber,
  parseNumberLength,
  validateInn,
  validateMaxLength,
  validateNumber,
  validateOnlyCyrillic,
  validateRequired,
  validateTel,
} from '../../helpers/ValidatorHelper';
import CheckBoxAdapter from '../FinalFormAdapters/CheckBoxAdapter';
import NumberFormatTelAdapter from '../NumberFormat/NumberFormatTelAdapter';
import { agentFileUpload, createRequest } from '../../api/agent';
import { AgentRequestRequest } from '../../structures/request/AgentRequestRequest';
import useClosableSnackbar from '../../hooks/useClosableSnackbar';
import DropZoneUploader from '../UI/DropZoneUploader/DropZoneUploader';
import { FileManaged } from '../../structures/response/FileManaged';
import { AgentRequestResponse } from '../../structures/response/AgentRequestResponse';
import { errorToString, scrollToError } from '../../helpers/Helper';
import { formatNumber } from '../../helpers/formatHelper';
import LegalSuggestAdapter from '../FinalFormAdapters/LegalSuggestAdapter';
import { DadataInnResponse } from '../../structures/response/dadata/DadataInnResponse';
import MfAlert from '../UI/Alert/MfAlert';

interface Props {
  onSubmitted: () => void;
  initialValues?: AgentRequestResponse | null;
}

const AgentRequestForm: FC<Props> = ({
  onSubmitted,
  initialValues = {} as AgentRequestResponse,
}) => {
  const [innResponse, setInnResponse] = useState<DadataInnResponse | null>(
    null
  );
  const { showSnackbar } = useClosableSnackbar();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const [uploadedFiles, setUploadedFiles] = useState(
    initialValues?.uploadedFiles || []
  );

  const submit = async (values: AgentRequestRequest) => {
    try {
      const r = await createRequest({
        ...values,
        contactPhone: parseNumber(values.contactPhone ?? ''),
        files: uploadedFiles?.map((v) => v.id),
        contactName: innResponse?.name?.full_with_opf,
      });
      if (r?.id) {
        await handleUploadFiles(selectedFiles, r.id);
        onSubmitted();
      }
    } catch (e) {
      showSnackbar(`Ошибка формирования заявки. ${errorToString(e)}`);
    }
  };

  const handleUploadFiles = async (
    files: File[],
    requestId: string | number
  ): Promise<FileManaged[]> => {
    const p = files.map((f) => agentFileUpload(f, requestId));
    return Promise.all(p);
  };

  const handleDrop = useCallback(
    async (files: File[]) => {
      setSelectedFiles(files);
    },
    [setSelectedFiles]
  );

  const handleDelete = async (file: Partial<FileManaged>) => {
    if (!file?.id) {
      return setSelectedFiles((v) => v.filter((f) => f !== file));
    }
    return setUploadedFiles((v) => v.filter((f) => f.id !== file.id));
  };

  const filesToDisplay = [];
  if (Array.isArray(uploadedFiles)) {
    filesToDisplay.push(...uploadedFiles);
  }
  if (Array.isArray(selectedFiles)) {
    filesToDisplay.push(...selectedFiles);
  }

  return (
    <div>
      <Form
        initialValues={initialValues}
        onSubmit={submit}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Field name="id" type="hidden" component="input" />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Field
                  name="inn"
                  label="ИНН"
                  component={LegalSuggestAdapter}
                  onItemSelected={(v: DadataInnResponse) => {
                    setInnResponse(v);
                  }}
                  getValue={(v: DadataInnResponse) => v.inn}
                  validate={composeValidators(validateRequired, validateInn)}
                />
                {Boolean(innResponse) && (
                  <MfAlert severity="info" sx={{ mt: 2 }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>
                        {innResponse?.name?.full_with_opf}
                      </span>
                    </div>
                  </MfAlert>
                )}
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="contactName"
                  label="Имя контактного лица"
                  component={TextFieldAdapter}
                  validate={validateOnlyCyrillic}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  type="tel"
                  name="contactPhone"
                  label="Телефон контактного лица"
                  validate={validateTel}
                  component={NumberFormatTelAdapter}
                  maxLength={10}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Field
                  name="amountRequested"
                  label="Пожелания к сумме займа"
                  component={TextFieldAdapter}
                  validate={composeValidators(
                    validateMaxLength(8),
                    validateNumber
                  )}
                  parse={parseNumberLength(8)}
                  format={(v) => (v ? formatNumber(+v, 0) : 0)}
                  endAdornment={
                    <InputAdornment position="start">₽</InputAdornment>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field
                  defaultValue="12"
                  name="termRequested"
                  label="Пожелания к сроку займа"
                  component={TextFieldAdapter}
                  validate={composeValidators(
                    validateMaxLength(2),
                    validateNumber
                  )}
                  parse={parseNumberLength(2)}
                  format={(v) => (v ? formatNumber(+v, 0) : 0)}
                  endAdornment={
                    <InputAdornment position="start">мес</InputAdornment>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="hasPledge"
                  label="У клиента есть транспортное средство для залога"
                  component={CheckBoxAdapter}
                  parse={(v) => +v}
                  type="checkbox"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  Документы при наличии (паспорт учредителя, промежуточная бух.
                  отчётность, банковская выписка за последние 6 месяцев для ИП и
                  др)
                </Box>
                <DropZoneUploader
                  onDrop={handleDrop}
                  onDelete={handleDelete}
                  uploaded={uploadedFiles || []}
                  selected={selectedFiles || []}
                  accept=".jpg,.png,.pdf,.txt,.jpeg"
                  acceptLabel="Поддерживаемые типы файлов: png, jpeg, jpg, pdf, txt"
                  errorText="Документы при наличии (паспорт учредителя, промежуточная бух.
                  отчётность, банковская выписка за последние 6 месяцев для ИП и
                  др)"
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="comment"
                  label="Комментарий"
                  component={TextFieldAdapter}
                  multiline
                />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'right' }}>
                <Button
                  type="submit"
                  onClick={() => {
                    scrollToError('.Mui-error', '.MuiGrid-container');
                  }}
                  variant="contained"
                  sx={{ my: '1rem' }}
                >
                  {initialValues?.id
                    ? 'Сохранить измененную копию заявки'
                    : 'Сохранить'}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      />
    </div>
  );
};

export default AgentRequestForm;
